import useCruiseOfferView, { isCruiseOnlyOfferView } from 'hooks/Cruise/useCruiseOfferView'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import type { SearchTileLogo } from '../SearchTile'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineJourney from '../shared/SearchTileRowOverlineJourney'
import CruiseSearchTileFeatures from './CruiseSearchTileFeatures'
import CruiseSearchTileLabels from './CruiseSearchTileLabels'
import CruiseSearchTileMeta from './CruiseSearchTileMeta'
import CruiseSearchTilePriceStack from './CruiseSearchTilePriceStack'
import CruiseSearchTileBundleCards from './CruiseSearchTileBundleCards'
import CruiseImageCornerLabels from './CruiseImageCornerLabels'
import TextButton from 'LuxKit/Button/TextButton'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { trimCruiseShipNameFromTitle } from 'lib/cruises/cruiseUtils'
import { getCheapestCruiseFlashOfferByCabin } from 'lib/cruises/getCheapestCruiseFlashOfferByCabin'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  filters?: App.OfferListFilters
}

function CruiseOfferTile({ offer, filters }: Props) {
  const intl = useIntl()
  const selectedCabinType = useCruiseSelectedCabinType(isCruiseOffer(offer) ? offer.cruiseType : undefined)
  const view = useCruiseOfferView({ offer, cabinTypes: selectedCabinType || undefined, filters })

  const logo = useMemo((): SearchTileLogo => ({
    compact: { id: view.cruiseLineImage, title: view.cruiseLineName },
    full: { id: view.cruiseLineImage, title: view.cruiseLineName },
  }), [view])

  const title = useMemo(() => trimCruiseShipNameFromTitle(offer.name), [offer.name])

  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const packagePricing = isCruiseOffer(offer) ? undefined : getCheapestCruiseFlashOfferByCabin({ offer, cabinType: selectedCabinType || '' }) || offer.lowestPricePackage
  const showPricingBlock = isCruiseOffer(offer) ?
      isDisplayablePrice(view.lowestPrice.promotionalPrice?.total ?? view.lowestPrice.primaryPrice.total) :
      hasDisplayablePackagePrice(packagePricing)
  const viewOfferLabel = intl.formatMessage({
    id: '1TDNDgzK3pnQZCGqQ7VW',
    defaultMessage: 'View offer',
    description: 'Button text to view cruise offer details in the cruise section',
  })

  const bundleCards = useMemo(() => {
    if (!isCruiseOnlyOfferView(view) || !view.lowestBundlePrice) {
      return undefined
    }

    const bundlePrice = view.lowestBundlePrice.promotionalPrice?.total ?? view.lowestBundlePrice.primaryPrice.total
    if (!isDisplayablePrice(bundlePrice)) {
      return undefined
    }

    return <CruiseSearchTileBundleCards view={view} filters={filters} viewOfferLabel={viewOfferLabel} />
  }, [view, filters, viewOfferLabel])

  const isBundleView = !!bundleCards

  return <SearchTile
    banner={<>{offerSiteTakeover?.searchPage?.tileBanner}</>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    images={offer.images}
    labels={<CruiseSearchTileLabels view={view} />}
    logo={logo}
    meta={<CruiseSearchTileMeta view={view} />}
    overline={<SearchTileRowOverlineJourney start={view.departureCity} end={view.destinationCity} />}
    productLabel={<CruiseImageCornerLabels view={view} countdownTagPlace="offer-search-tile" />}
    title={title}
    offer={offer}
    action={isBundleView ? <span aria-hidden="true" /> : <TextButton nonInteractable kind="primary">{viewOfferLabel}</TextButton>}
    features={!isBundleView && <CruiseSearchTileFeatures view={view} filters={filters} />}
    pricePoint={!isBundleView && showPricingBlock && <CruiseSearchTilePriceStack view={view} />}
    bottomExtension={bundleCards}
  />
}

export default CruiseOfferTile
